import React, { useState } from 'react'

const API = process.env.REACT_APP_API_URL || 'https://sunshoos-v2.onrender.com'
const fmt = n => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const SECTIONS = [
  { key: 'employment',     label: 'SA102 Employment',          icon: '💼' },
  { key: 'self_employment',label: 'SA103 Self-Employment',     icon: '🧾' },
  { key: 'property',       label: 'SA105 Property Income',     icon: '🏠' },
  { key: 'dividends',      label: 'Dividends & Savings',       icon: '📈' },
  { key: 'capital_gains',  label: 'SA108 Capital Gains',       icon: '📊' },
]

export default function SA100() {
  const [tab, setTab] = useState('manual')
  const [activeSection, setActiveSection] = useState('employment')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [saved, setSaved] = useState(false)

  // Personal details
  const [personal, setPersonal] = useState({
    full_name: '', utr: '', nino: '', dob: '',
    address: '', tax_year: '2024/25',
    student_loan: false, blind_persons_allowance: false,
  })
  const setP = (k, v) => setPersonal(p => ({ ...p, [k]: v }))

  // Income sections
  const [employment, setEmployment] = useState({
    employer_name: '', gross_pay: '', tax_deducted: '',
    benefits_in_kind: '', expenses_claimed: '',
  })
  const [selfEmp, setSelfEmp] = useState({
    business_name: '', business_type: '',
    turnover: '', expenses: '', net_profit: '',
    capital_allowances: '', loss_bf: '',
  })
  const [property, setProperty] = useState({
    rental_income: '', mortgage_interest: '',
    repairs: '', management_fees: '', other_expenses: '',
    furnished_holiday_let: false,
  })
  const [dividends, setDividends] = useState({
    uk_dividends: '', foreign_dividends: '',
    bank_interest: '', other_savings: '',
  })
  const [capitalGains, setCapitalGains] = useState({
    residential_proceeds: '', residential_cost: '', residential_improvements: '',
    other_proceeds: '', other_cost: '', other_improvements: '',
    losses_current: '', losses_bf: '',
  })

  // P&L import
  const [plJson, setPlJson] = useState('')

  const setField = (setter, k, v) => setter(s => ({ ...s, [k]: v }))

  async function handleSubmit() {
    setLoading(true); setError(''); setResult(null); setSaved(false)
    try {
      let selfEmpData = { ...selfEmp }
      if (tab === 'pl' && plJson.trim()) {
        try {
          const pl = JSON.parse(plJson)
          const ga = k => Math.abs(pl[k] || 0)
          selfEmpData.turnover = ga('Sales/Income').toString()
          selfEmpData.expenses = (
            ga('Purchases') + ga('Other Direct Costs') + ga('Administration and office expenses') +
            ga('Advertising and promotions') + ga('Travel and subsistence') + ga('Vehicle expenses ') +
            ga('Insurance') + ga('Repairs, renewals and maintenance.') + ga('Sundry expenses (small miscellaneous expenses only)')
          ).toString()
          selfEmpData.net_profit = (ga('Sales/Income') - parseFloat(selfEmpData.expenses)).toString()
          selfEmpData.capital_allowances = (ga('Depreciation')).toString()
        } catch { throw new Error('Invalid P&L JSON') }
      }
      const res = await fetch(`${API}/api/sa100/compute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personal, employment, self_employment: selfEmpData, property, dividends, capital_gains: capitalGains })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  async function handleExport(type) {
    try {
      const res = await fetch(`${API}/api/sa100/export-${type}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error(`${type} export failed`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `SA100_${(result.personal?.full_name || 'Client').replace(/ /g, '_')}_${(result.personal?.tax_year || '').replace(/\//g, '-')}.${type === 'excel' ? 'xlsx' : 'pdf'}`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError(`Export error: ${e.message}`) }
  }

  async function handleSave() {
    try {
      const res = await fetch(`${API}/api/sa100/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Save failed')
      setSaved(true)
    } catch (e) { setError('Save error: ' + e.message) }
  }

  const s = {
    page: { padding: '24px', maxWidth: 1020, margin: '0 auto', fontFamily: 'Calibri, sans-serif' },
    card: { background: '#0f1923', border: '1px solid rgba(201,151,58,0.15)', borderRadius: 10, padding: 22, marginBottom: 18 },
    row: { display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
    label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    input: { background: '#0B1929', border: '1px solid rgba(201,151,58,0.2)', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    tabBar: { display: 'flex', gap: 2, marginBottom: 18, background: '#0B1929', borderRadius: 8, padding: 4 },
    tab: a => ({ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: a ? '#C9973A' : 'transparent', color: a ? '#0B1929' : 'rgba(255,255,255,0.4)', transition: 'all .2s' }),
    btn: (v = 'primary') => ({ padding: '10px 20px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: v === 'primary' ? '#C9973A' : v === 'green' ? '#1a4a2e' : v === 'blue' ? '#0f2a4a' : '#1a2a4a', color: v === 'primary' ? '#0B1929' : v === 'green' ? '#4ade80' : v === 'blue' ? '#93c5fd' : '#C9973A' }),
    sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(201,151,58,0.7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    sectionTab: a => ({ padding: '8px 14px', borderRadius: 7, border: `1px solid ${a ? 'rgba(201,151,58,0.5)' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer', fontSize: 12, fontWeight: a ? 700 : 400, background: a ? 'rgba(201,151,58,0.1)' : 'transparent', color: a ? '#C9973A' : 'rgba(255,255,255,0.4)', marginRight: 8, marginBottom: 8 }),
    summaryRow: (bold, color) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: bold ? '11px 16px' : '8px 16px', borderRadius: 6, marginBottom: 4, background: bold ? 'rgba(255,255,255,0.04)' : 'transparent', borderBottom: bold ? 'none' : '1px solid rgba(255,255,255,0.05)' }),
    checkbox: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  }

  const inp = (val, setter, k, label, type = 'number', placeholder = '0.00') => (
    <div style={{ flex: 1, minWidth: 160 }}>
      <label style={s.label}>{label}</label>
      <input style={s.input} type={type} value={val[k]} onChange={e => setter(v => ({ ...v, [k]: e.target.value }))} placeholder={placeholder} />
    </div>
  )

  const tax = result?.tax_computation || {}
  const summary = result?.income_summary || {}

  return (
    <div style={s.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>Self Assessment SA100</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0' }}>
          SA102 · SA103 · SA105 · SA108 · Dividends · 2024/25 tax year · Excel + PDF
        </p>
      </div>

      {/* Personal Details */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Personal Details</div>
        <div style={s.row}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={s.label}>Full Name</label>
            <input style={s.input} value={personal.full_name} onChange={e => setP('full_name', e.target.value)} placeholder="e.g. John Smith" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>UTR Number</label>
            <input style={s.input} value={personal.utr} onChange={e => setP('utr', e.target.value)} placeholder="e.g. 1234567890" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>NI Number</label>
            <input style={s.input} value={personal.nino} onChange={e => setP('nino', e.target.value)} placeholder="e.g. AB123456C" />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label style={s.label}>Tax Year</label>
            <select style={s.input} value={personal.tax_year} onChange={e => setP('tax_year', e.target.value)}>
              <option>2024/25</option><option>2023/24</option><option>2022/23</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={s.label}>Date of Birth</label>
            <input style={s.input} type="date" value={personal.dob} onChange={e => setP('dob', e.target.value)} />
          </div>
        </div>
        <div style={s.row}>
          <div style={{ flex: 3, minWidth: 260 }}>
            <label style={s.label}>Address</label>
            <input style={s.input} value={personal.address} onChange={e => setP('address', e.target.value)} placeholder="Full address including postcode" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <label style={s.checkbox}>
            <input type="checkbox" checked={personal.student_loan} onChange={e => setP('student_loan', e.target.checked)} />
            Student loan repayment
          </label>
          <label style={s.checkbox}>
            <input type="checkbox" checked={personal.blind_persons_allowance} onChange={e => setP('blind_persons_allowance', e.target.checked)} />
            Blind person's allowance
          </label>
        </div>
      </div>

      {/* Input method tabs */}
      <div style={s.tabBar}>
        <button style={s.tab(tab === 'manual')} onClick={() => setTab('manual')}>✏️ Manual Entry</button>
        <button style={s.tab(tab === 'pl')} onClick={() => setTab('pl')}>📊 Import Self-Employment from P&amp;L</button>
      </div>

      {/* P&L import for self-employment */}
      {tab === 'pl' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Import Self-Employment from P&amp;L</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
            Paste <code style={{ color: '#C9973A' }}>pl_totals</code> JSON from Bank Stmt → P&L. Turnover, expenses and capital allowances will be auto-filled for SA103. Then add employment, property, dividends and capital gains below.
          </p>
          <textarea style={{ ...s.input, height: 130, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
            value={plJson} onChange={e => setPlJson(e.target.value)}
            placeholder={'{\n  "Sales/Income": 65000,\n  "Purchases": 12000,\n  ...\n}'} />
        </div>
      )}

      {/* Income sections */}
      <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 14 }}>
        {SECTIONS.map(sec => (
          <button key={sec.key} style={s.sectionTab(activeSection === sec.key)} onClick={() => setActiveSection(sec.key)}>
            {sec.icon} {sec.label}
          </button>
        ))}
      </div>

      {/* SA102 Employment */}
      {activeSection === 'employment' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>SA102 — Employment Income</div>
          <div style={s.row}>
            {inp(employment, setEmployment, 'employer_name', 'Employer Name', 'text', 'e.g. ABC Ltd')}
            {inp(employment, setEmployment, 'gross_pay', 'Gross Pay (£)')}
            {inp(employment, setEmployment, 'tax_deducted', 'Tax Deducted (£)')}
          </div>
          <div style={s.row}>
            {inp(employment, setEmployment, 'benefits_in_kind', 'Benefits in Kind (£)')}
            {inp(employment, setEmployment, 'expenses_claimed', 'Expenses Claimed (£)')}
          </div>
        </div>
      )}

      {/* SA103 Self-Employment */}
      {activeSection === 'self_employment' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>SA103 — Self-Employment</div>
          <div style={s.row}>
            {inp(selfEmp, setSelfEmp, 'business_name', 'Business Name', 'text', 'e.g. John Smith Consulting')}
            {inp(selfEmp, setSelfEmp, 'business_type', 'Business Type', 'text', 'e.g. IT Consultancy')}
          </div>
          <div style={s.row}>
            {inp(selfEmp, setSelfEmp, 'turnover', 'Turnover (£)')}
            {inp(selfEmp, setSelfEmp, 'expenses', 'Total Allowable Expenses (£)')}
            {inp(selfEmp, setSelfEmp, 'net_profit', 'Net Profit (£)')}
          </div>
          <div style={s.row}>
            {inp(selfEmp, setSelfEmp, 'capital_allowances', 'Capital Allowances (£)')}
            {inp(selfEmp, setSelfEmp, 'loss_bf', 'Losses Brought Forward (£)')}
          </div>
        </div>
      )}

      {/* SA105 Property */}
      {activeSection === 'property' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>SA105 — Property Income</div>
          <div style={s.row}>
            {inp(property, setProperty, 'rental_income', 'Total Rental Income (£)')}
            {inp(property, setProperty, 'mortgage_interest', 'Mortgage Interest (£)')}
            {inp(property, setProperty, 'repairs', 'Repairs & Maintenance (£)')}
          </div>
          <div style={s.row}>
            {inp(property, setProperty, 'management_fees', 'Management Fees (£)')}
            {inp(property, setProperty, 'other_expenses', 'Other Allowable Expenses (£)')}
          </div>
          <label style={s.checkbox}>
            <input type="checkbox" checked={property.furnished_holiday_let} onChange={e => setProperty(p => ({ ...p, furnished_holiday_let: e.target.checked }))} />
            Furnished Holiday Let (FHL)
          </label>
        </div>
      )}

      {/* Dividends & Savings */}
      {activeSection === 'dividends' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Dividends &amp; Savings Income</div>
          <div style={s.row}>
            {inp(dividends, setDividends, 'uk_dividends', 'UK Dividends (£)')}
            {inp(dividends, setDividends, 'foreign_dividends', 'Foreign Dividends (£)')}
          </div>
          <div style={s.row}>
            {inp(dividends, setDividends, 'bank_interest', 'Bank / Building Society Interest (£)')}
            {inp(dividends, setDividends, 'other_savings', 'Other Savings Income (£)')}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 8 }}>
            2024/25: Dividend allowance £500 · Personal savings allowance £1,000 (basic rate) / £500 (higher rate)
          </div>
        </div>
      )}

      {/* SA108 Capital Gains */}
      {activeSection === 'capital_gains' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>SA108 — Capital Gains</div>
          <div style={{ ...s.sectionTitle, fontSize: 10, marginTop: 0 }}>Residential Property</div>
          <div style={s.row}>
            {inp(capitalGains, setCapitalGains, 'residential_proceeds', 'Proceeds (£)')}
            {inp(capitalGains, setCapitalGains, 'residential_cost', 'Original Cost (£)')}
            {inp(capitalGains, setCapitalGains, 'residential_improvements', 'Improvements (£)')}
          </div>
          <div style={{ ...s.sectionTitle, fontSize: 10, marginTop: 4 }}>Other Assets</div>
          <div style={s.row}>
            {inp(capitalGains, setCapitalGains, 'other_proceeds', 'Proceeds (£)')}
            {inp(capitalGains, setCapitalGains, 'other_cost', 'Original Cost (£)')}
            {inp(capitalGains, setCapitalGains, 'other_improvements', 'Improvements (£)')}
          </div>
          <div style={s.row}>
            {inp(capitalGains, setCapitalGains, 'losses_current', 'Losses This Year (£)')}
            {inp(capitalGains, setCapitalGains, 'losses_bf', 'Losses Brought Forward (£)')}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 8 }}>
            2024/25: Annual exempt amount £3,000 · Residential CGT: 18%/24% · Other assets: 10%/20%
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <button style={s.btn('primary')} onClick={handleSubmit} disabled={loading}>
          {loading ? '⏳ Computing tax…' : '▶ Compute SA100 Tax Return'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#f87171', fontSize: 13 }}>⚠ {error}</div>
      )}

      {/* Results */}
      {result && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{result.personal?.full_name} — SA100 {result.personal?.tax_year}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>UTR: {result.personal?.utr || '—'} · NI: {result.personal?.nino || '—'}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={s.btn('green')} onClick={() => handleExport('excel')}>⬇ Excel</button>
              <button style={s.btn('blue')} onClick={() => handleExport('pdf')}>⬇ PDF</button>
              <button style={s.btn('save')} onClick={handleSave} disabled={saved}>{saved ? '✓ Saved' : '💾 Save'}</button>
            </div>
          </div>

          {/* Income summary */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Income Summary</div>
            {[
              ['Employment Income',       summary.employment_income],
              ['Self-Employment Profit',  summary.self_employment_profit],
              ['Property Income',         summary.property_profit],
              ['Dividends',               summary.total_dividends],
              ['Savings Interest',        summary.total_savings],
              ['Capital Gains',           summary.total_capital_gains],
            ].map(([label, val]) => val > 0 && (
              <div key={label} style={s.summaryRow(false)}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{label}</div>
                <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{fmt(val)}</div>
              </div>
            ))}
            <div style={{ ...s.summaryRow(true), marginTop: 8, background: 'rgba(201,151,58,0.08)', borderRadius: 7, border: '1px solid rgba(201,151,58,0.2)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Total Income</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#C9973A' }}>{fmt(summary.total_income)}</div>
            </div>
          </div>

          {/* Tax computation */}
          <div style={s.card}>
            <div style={s.sectionTitle}>Tax Computation</div>
            {[
              { label: 'Total Income',               val: tax.total_income,           bold: false },
              { label: 'Less: Personal Allowance',   val: tax.personal_allowance,     bold: false },
              { label: 'Taxable Income',             val: tax.taxable_income,         bold: true, highlight: 'gold' },
              { label: 'Income Tax',                 val: tax.income_tax,             bold: false },
              { label: 'Dividend Tax',               val: tax.dividend_tax,           bold: false },
              { label: 'CGT',                        val: tax.cgt,                    bold: false },
              { label: 'Less: Tax Already Paid',     val: tax.tax_already_paid,       bold: false },
              { label: 'Class 4 NIC',                val: tax.class4_nic,             bold: false },
              { label: 'Class 2 NIC',                val: tax.class2_nic,             bold: false },
              { label: 'Total Tax & NIC Due',        val: tax.total_due,              bold: true, highlight: 'green' },
            ].map(({ label, val, bold, highlight }) => (val !== undefined && val !== null) && (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: bold ? '11px 16px' : '8px 16px', borderRadius: bold ? 7 : 0, marginBottom: bold ? 6 : 0, background: highlight === 'gold' ? 'rgba(201,151,58,0.08)' : highlight === 'green' ? 'rgba(74,222,128,0.08)' : 'transparent', border: highlight ? `1px solid ${highlight === 'gold' ? 'rgba(201,151,58,0.2)' : 'rgba(74,222,128,0.2)'}` : 'none', borderBottom: !highlight && !bold ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                <div style={{ fontSize: bold ? 13 : 12, fontWeight: bold ? 700 : 400, color: bold ? '#fff' : 'rgba(255,255,255,0.6)' }}>{label}</div>
                <div style={{ fontSize: bold ? 15 : 12, fontWeight: bold ? 800 : 500, color: highlight === 'green' ? '#4ade80' : highlight === 'gold' ? '#C9973A' : '#fff' }}>{fmt(val)}</div>
              </div>
            ))}

            {/* Payments on account */}
            {tax.payment_on_account > 0 && (
              <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 7, background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.2)' }}>
                <div style={{ fontSize: 12, color: '#93c5fd', fontWeight: 700 }}>Payments on Account</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                  31 Jan {new Date().getFullYear() + 1}: {fmt(tax.payment_on_account)} · 31 Jul {new Date().getFullYear() + 1}: {fmt(tax.payment_on_account)}
                </div>
              </div>
            )}
          </div>

          {/* Flags */}
          {result.flags?.length > 0 && (
            <div style={s.card}>
              <div style={s.sectionTitle}>Review Flags</div>
              {result.flags.map((flag, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '9px 12px', background: 'rgba(255,170,0,0.06)', borderRadius: 6, marginBottom: 5, border: '1px solid rgba(255,170,0,0.15)' }}>
                  <span style={{ color: '#FBBF24' }}>⚠</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>{flag}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
